import { useEffect, useState, useCallback } from "react";
import api from "../api";
import { Modal } from "../components/ui";

const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const REGIME_ORDER = ["forfettario", "semplificato", "ordinario"];
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "";

const WHEN_MONTH = { marzo: 3, giugno: 6, luglio: 7, dicembre: 12 };

export function dueStats(client, year, monthlyTasks, annualTasks) {
  const now = new Date();
  const curY = now.getFullYear();
  const prevMonths = now.getMonth(); // mesi completati: fino al mese PRECEDENTE (a luglio → 6, cioè gen-giu)
  // Ordinari/semplificati: contano i mesi fino al precedente.
  // Forfettari: le registrazioni mensili rilevano solo ad anno concluso.
  const isForf = client.regime === "forfettario";
  let monthsDue;
  if (year < curY) monthsDue = 12;
  else if (year > curY) monthsDue = 0;
  else monthsDue = isForf ? 0 : prevMonths;
  let due = 0, done = 0;
  for (let m = 1; m <= monthsDue; m++) {
    for (const t of monthlyTasks) { due += 1; if (client.checks[`m${m}:${t.key}`]) done += 1; }
  }
  for (const t of annualTasks) {
    const wm = WHEN_MONTH[t.when] || 12;
    if (year < curY || (year === curY && wm <= prevMonths)) {
      due += 1; if (client.checks[`a:${t.key}`]) done += 1;
    }
  }
  return { due, done, ratio: due > 0 ? done / due : 1 };
}

export function Donut({ ratio, size = 30, testid }) {
  const full = ratio >= 0.999;
  const color = full ? "#39ff14" : ratio >= 0.7 ? "#a3e635" : ratio >= 0.4 ? "#facc15" : "#fb7185";
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} data-testid={testid}
      style={full ? { filter: "drop-shadow(0 0 5px #39ff14)" } : undefined} role="img" aria-label={`${Math.round(ratio * 100)}%`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(148,163,184,.2)" strokeWidth="5" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="5"
        strokeDasharray={`${circ * ratio} ${circ}`} strokeLinecap={ratio > 0 ? "round" : "butt"}
        transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      {full && <text x="50%" y="54%" dominantBaseline="middle" textAnchor="middle" fontSize={size * 0.4} fill="#39ff14">✓</text>}
    </svg>
  );
}

function cellColor(done, total) {
  if (total === 0) return {};
  if (done === total) return { background: "rgba(34,197,94,.18)", color: "#4ade80", border: "1px solid rgba(34,197,94,.4)" };
  if (done > 0) return { background: "rgba(250,204,21,.14)", color: "#facc15", border: "1px solid rgba(250,204,21,.35)" };
  return { background: "rgba(148,163,184,.08)", color: "#64748b", border: "1px solid rgba(148,163,184,.2)" };
}

export default function Contabilita() {
  const [year, setYear] = useState(2026);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("tutti");
  const [cell, setCell] = useState(null); // {client, period, label}
  const [role, setRole] = useState("admin");
  const [myName, setMyName] = useState("tu");
  const [selIds, setSelIds] = useState(new Set());
  const [bulkRegime, setBulkRegime] = useState("forfettario");
  const [msg, setMsg] = useState("");
  const [bkFrom, setBkFrom] = useState(1);
  const [bkTo, setBkTo] = useState(1);
  const [bkTasks, setBkTasks] = useState(new Set());
  const [bkAnnual, setBkAnnual] = useState(new Set());
  useEffect(() => { setSelIds(new Set()); setBkTasks(new Set()); setBkAnnual(new Set()); }, [tab]);

  useEffect(() => { api.get("/auth/me").then((r) => { setRole(r.data.role || "admin"); setMyName(r.data.name || r.data.email || "tu"); }).catch(() => {}); }, []);
  const load = useCallback(() => {
    api.get(`/contabilita/board?year=${year}`).then((r) => setData(r.data)).catch(() => {});
  }, [year]);
  useEffect(() => { load(); }, [load]);
  // la lista resta allineata ai Contatti: refresh automatico ogni 60s (se nessuna modale aperta)
  useEffect(() => {
    const t = setInterval(() => { if (!cell) load(); }, 60000);
    return () => clearInterval(t);
  }, [load, cell]);

  if (!data) return <div><h1 className="page-title">Situazione registrazioni</h1><p className="muted">Caricamento…</p></div>;

  const tasks = data.tasks;
  const clients = data.clients || [];
  const byRegime = (r) => r === "tutti" ? clients.filter((c) => REGIME_ORDER.includes(c.regime))
    : r === "ord_semp" ? clients.filter((c) => c.regime === "ordinario" || c.regime === "semplificato")
    : clients.filter((c) => c.regime === r);
  const unassigned = clients.filter((c) => !c.regime);
  const soloConsulenza = clients.filter((c) => c.regime === "solo_consulenza");
  const isAdmin = role !== "collaboratore";

  const cMonthly = (c) => (tasks[c.regime]?.monthly || []).filter((t) => !(c.excluded || []).includes(t.key));
  const cAnnual = (c) => (tasks[c.regime]?.annual || []).filter((t) => !(c.excluded || []).includes(t.key));
  const monthlyTasks = tab === "tutti"
    ? Object.values(tasks).flatMap((r) => r.monthly).filter((t, i, arr) => arr.findIndex((x) => x.key === t.key) === i)
    : tab === "ord_semp"
    ? ["ordinario", "semplificato"].flatMap((r) => tasks[r]?.monthly || []).filter((t, i, arr) => arr.findIndex((x) => x.key === t.key) === i)
    : (tasks[tab]?.monthly || []);
  const annualTasks = tab === "tutti" ? []
    : tab === "ord_semp"
    ? ["ordinario", "semplificato"].flatMap((r) => tasks[r]?.annual || []).filter((t, i, arr) => arr.findIndex((x) => x.key === t.key) === i)
    : (tasks[tab]?.annual || []);

  const doneInPeriod = (client, period, list) => list.filter((t) => client.checks[`${period}:${t.key}`]).length;

  const toggle = async (client, period, task, done) => {
    await api.post("/contabilita/check", { company_id: client.id, year, period, task, done });
    // aggiornamento ottimistico
    setData((d) => {
      const clients2 = d.clients.map((c) => {
        if (c.id !== client.id) return c;
        const checks = { ...c.checks };
        if (done) checks[`${period}:${task}`] = { done_by: myName, done_at: new Date().toISOString() };
        else delete checks[`${period}:${task}`];
        return { ...c, checks };
      });
      return { ...d, clients: clients2 };
    });
    setCell((cl) => cl && { ...cl, client: { ...cl.client, checks: (() => { const ch = { ...cl.client.checks }; if (done) ch[`${period}:${task}`] = { done_by: myName, done_at: new Date().toISOString() }; else delete ch[`${period}:${task}`]; return ch; })() } });
  };

  const setRegimeBulk = async (ids, regime) => {
    try {
      const r = await api.post("/contabilita/regime", { company_ids: ids, regime });
      setMsg(regime ? `✅ Regime "${regime}" impostato per ${r.data.updated} clienti.` : `✅ Regime rimosso per ${r.data.updated} clienti.`);
      setSelIds(new Set());
      load();
    } catch (e) { setMsg("Errore: " + (e.response?.data?.detail || e.message)); }
  };

  const setExcluded = async (ids, taskKeys, excluded) => {
    try {
      const r = await api.post("/contabilita/exclude", { company_ids: ids, tasks: taskKeys, excluded });
      setMsg(excluded ? `🚫 Adempimenti segnati "non previsti" per ${r.data.updated} clienti.` : `✅ Adempimenti riattivati per ${r.data.updated} clienti.`);
      // aggiornamento ottimistico anche del modale aperto
      const upd = (c) => ids.includes(c.id)
        ? { ...c, excluded: excluded ? [...new Set([...(c.excluded || []), ...taskKeys])] : (c.excluded || []).filter((k) => !taskKeys.includes(k)) }
        : c;
      setData((d) => ({ ...d, clients: d.clients.map(upd) }));
      setCell((cl) => cl && { ...cl, client: upd(cl.client) });
    } catch (e) { setMsg("Errore: " + (e.response?.data?.detail || e.message)); }
  };

  const applyBulkChecks = async (done) => {
    const a = Math.min(bkFrom, bkTo), b = Math.max(bkFrom, bkTo);
    const periods = [];
    for (let m = a; m <= b; m++) periods.push(`m${m}`);
    const selected = tabClients.filter((c) => selIds.has(c.id));
    // raggruppa per regime: a ogni cliente si applicano solo i task del SUO regime
    const groups = {};
    for (const c of selected) (groups[c.regime] = groups[c.regime] || []).push(c.id);
    const calls = [];
    for (const [regime, ids] of Object.entries(groups)) {
      const mk = (tasks[regime]?.monthly || []).map((t) => t.key).filter((k) => bkTasks.has(k));
      const ak = (tasks[regime]?.annual || []).map((t) => t.key).filter((k) => bkAnnual.has(k));
      if (mk.length > 0 && periods.length > 0)
        calls.push(api.post("/contabilita/check-bulk", { company_ids: ids, year, periods, tasks: mk, done }));
      if (ak.length > 0)
        calls.push(api.post("/contabilita/check-bulk", { company_ids: ids, year, periods: ["a"], tasks: ak, done }));
    }
    if (calls.length === 0) { setMsg("Scegli almeno un tipo di adempimento."); return; }
    try {
      await Promise.all(calls);
      setMsg(`✅ ${done ? "Segnati fatti" : "Riportati da fare"} per ${selected.length} clienti.`);
      load();
    } catch (e) { setMsg("Errore: " + (e.response?.data?.detail || e.message)); }
  };

  const tabClients = byRegime(tab === "na" ? "" : tab);
  const tabAgg = tabClients.reduce((acc, c) => {
    const ds = dueStats(c, year, cMonthly(c), cAnnual(c));
    return { due: acc.due + ds.due, done: acc.done + ds.done };
  }, { due: 0, done: 0 });

  return (
    <div>
      <h1 className="page-title">Situazione registrazioni</h1>
      <p className="page-sub">Avanzamento registrazioni e dichiarazioni per cliente. Ogni spunta registra chi l'ha fatta e quando.</p>

      <div className="toolbar" style={{ gap: 10, flexWrap: "wrap" }}>
        <span className="muted">Anno</span>
        <button className="btn sm" onClick={() => setYear((y) => y - 1)} data-testid="year-prev">◀</button>
        <b data-testid="year-label">{year}</b>
        <button className="btn sm" onClick={() => setYear((y) => y + 1)} data-testid="year-next">▶</button>
        <div className="spacer" />
        <div className="tabs" style={{ marginBottom: 0 }}>
          <button className={"tab" + (tab === "tutti" ? " active" : "")} onClick={() => setTab("tutti")} data-testid="tab-tutti">
            Tutti ({byRegime("tutti").length})
          </button>
          {REGIME_ORDER.map((r) => (
            <button key={r} className={"tab" + (tab === r ? " active" : "")} onClick={() => setTab(r)} data-testid={`tab-${r}`}>
              {tasks[r].label} ({byRegime(r).length})
            </button>
          ))}
          <button className={"tab" + (tab === "ord_semp" ? " active" : "")} onClick={() => setTab("ord_semp")} data-testid="tab-ord-semp">
            Ord. + Sempl. ({byRegime("ord_semp").length})
          </button>
          {isAdmin && (
            <button className={"tab" + (tab === "na" ? " active" : "")} onClick={() => setTab("na")} data-testid="tab-na">
              Da assegnare ({unassigned.length})
            </button>
          )}
          {isAdmin && soloConsulenza.length > 0 && (
            <button className={"tab" + (tab === "sc" ? " active" : "")} onClick={() => setTab("sc")} data-testid="tab-sc">
              Solo consulenza ({soloConsulenza.length})
            </button>
          )}
        </div>
      </div>
      {msg && <p className="muted" data-testid="contab-msg">{msg}</p>}

      {tab === "sc" ? (
        <div className="panel">
          <h3 style={{ marginTop: 0 }}>Clienti solo consulenza / dichiarazioni</h3>
          <p className="muted">Restano clienti a tutti gli effetti (email, fatture, margini) ma non sono monitorati nella griglia contabilità. Puoi riportarli in un regime in qualsiasi momento.</p>
          <table className="table" style={{ width: "100%" }}>
            <tbody>
              {soloConsulenza.map((c) => (
                <tr key={c.id} data-testid={`sc-row-${c.id}`}>
                  <td>{c.name}</td>
                  <td style={{ textAlign: "right" }}>
                    {REGIME_ORDER.map((r) => (
                      <button key={r} className="btn sm" style={{ marginLeft: 6 }} onClick={() => setRegimeBulk([c.id], r)} data-testid={`sc-set-${r}-${c.id}`}>{tasks[r].label}</button>
                    ))}
                    <button className="btn sm" style={{ marginLeft: 6, opacity: .75 }} onClick={() => setRegimeBulk([c.id], null)} data-testid={`sc-unset-${c.id}`}>Da assegnare</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : tab === "na" ? (
        <div className="panel">
          <h3 style={{ marginTop: 0 }}>Clienti senza regime contabile</h3>
          <div className="toolbar" style={{ gap: 8 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input type="checkbox" checked={selIds.size === unassigned.length && unassigned.length > 0}
                onChange={(e) => setSelIds(e.target.checked ? new Set(unassigned.map((c) => c.id)) : new Set())} data-testid="na-select-all" />
              <span className="muted">Seleziona tutti</span>
            </label>
            <div className="spacer" />
            <span className="muted">{selIds.size} selezionati →</span>
            <select value={bulkRegime} onChange={(e) => setBulkRegime(e.target.value)} data-testid="bulk-regime-select">
              {REGIME_ORDER.map((r) => <option key={r} value={r}>{tasks[r].label}</option>)}
            </select>
            <button className="btn primary sm" disabled={selIds.size === 0} onClick={() => setRegimeBulk([...selIds], bulkRegime)} data-testid="bulk-regime-apply">Imposta regime</button>
          </div>
          <table className="table" style={{ width: "100%" }}>
            <tbody>
              {unassigned.map((c) => (
                <tr key={c.id} data-testid={`na-row-${c.id}`}>
                  <td style={{ width: 30 }}><input type="checkbox" checked={selIds.has(c.id)} onChange={(e) => setSelIds((s) => { const n = new Set(s); e.target.checked ? n.add(c.id) : n.delete(c.id); return n; })} /></td>
                  <td>{c.name}</td>
                  <td style={{ textAlign: "right" }}>
                    {REGIME_ORDER.map((r) => (
                      <button key={r} className="btn sm" style={{ marginLeft: 6 }} onClick={() => setRegimeBulk([c.id], r)} data-testid={`na-set-${r}-${c.id}`}>{tasks[r].label}</button>
                    ))}
                    <button className="btn sm" style={{ marginLeft: 6, opacity: .75 }} onClick={() => setRegimeBulk([c.id], "solo_consulenza")} title="Cliente solo consulenze/dichiarazioni: non monitorato in contabilità" data-testid={`na-set-sc-${c.id}`}>Solo consulenza</button>
                  </td>
                </tr>
              ))}
              {unassigned.length === 0 && <tr><td className="muted">Tutti i clienti hanno un regime assegnato. 🎉</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="panel">
          <div className="toolbar" style={{ gap: 12, marginTop: 0 }}>
            <Donut ratio={tabAgg.due > 0 ? tabAgg.done / tabAgg.due : 0} size={44} testid="tab-donut" />
            <div>
              <b data-testid="tab-agg">{tabAgg.due > 0 ? `${Math.round((tabAgg.done / tabAgg.due) * 100)}% aggiornato` : "Nessun adempimento ancora dovuto"}</b>
              <div className="muted" style={{ fontSize: 12 }}>
                {tabAgg.due > 0
                  ? `${tabAgg.done}/${tabAgg.due} adempimenti dovuti fino al mese scorso (${tabClients.length} clienti)`
                  : tab === "forfettario"
                  ? `Le registrazioni dei forfettari rilevano a fine anno (${tabClients.length} clienti)`
                  : `Gli adempimenti del ${year} inizieranno a contare da gennaio ${year} (${tabClients.length} clienti)`}
              </div>
            </div>
          </div>
          <p className="muted" style={{ marginTop: 6, fontSize: 12 }}>
            Mensile: {monthlyTasks.map((t) => t.label).join(" · ")}
            {annualTasks.length > 0 && <> — Annuale: {annualTasks.map((t) => `${t.label} (${t.when}${t.fiscal === "prev" ? `, anno ${year - 1}` : ""})`).join(" · ")}</>}
          </p>
          <details className="bulk-details" open={typeof window !== "undefined" && window.innerWidth > 760} data-testid="bulk-check-bar">
            <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 800, padding: "8px 10px" }}>
              Spunta massiva <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>({selIds.size} clienti selezionati)</span>
            </summary>
          <div className="toolbar" style={{ gap: 8, flexWrap: "wrap", padding: "0 10px 8px", marginTop: 0 }}>
            <span className="muted">mesi</span>
            <select style={{ maxWidth: 90 }} value={bkFrom} onChange={(e) => setBkFrom(+e.target.value)} data-testid="bulk-month-from">
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <span className="muted">→</span>
            <select style={{ maxWidth: 90 }} value={bkTo} onChange={(e) => setBkTo(+e.target.value)} data-testid="bulk-month-to">
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            {monthlyTasks.map((t) => (
              <label key={t.key} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
                <input type="checkbox" checked={bkTasks.has(t.key)} onChange={(e) => setBkTasks((s) => { const n = new Set(s); e.target.checked ? n.add(t.key) : n.delete(t.key); return n; })} data-testid={`bulk-task-${t.key}`} />
                {t.label}
              </label>
            ))}
            {annualTasks.length > 0 && <span className="muted" style={{ marginLeft: 8 }}>annuali:</span>}
            {annualTasks.map((t) => (
              <label key={t.key} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
                <input type="checkbox" checked={bkAnnual.has(t.key)} onChange={(e) => setBkAnnual((s) => { const n = new Set(s); e.target.checked ? n.add(t.key) : n.delete(t.key); return n; })} data-testid={`bulk-annual-${t.key}`} />
                {t.label}
              </label>
            ))}
            <button className="btn sm primary" disabled={selIds.size === 0} onClick={() => applyBulkChecks(true)} data-testid="bulk-check-done">✓ Segna fatti</button>
            <button className="btn sm" disabled={selIds.size === 0} onClick={() => applyBulkChecks(false)} data-testid="bulk-check-undone">↩ Riporta da fare</button>
            {isAdmin && <button className="btn sm" disabled={selIds.size === 0 || (bkTasks.size === 0 && bkAnnual.size === 0)}
              title="I tipi di adempimento spuntati qui sopra non saranno più conteggiati per i clienti selezionati (es. cliente senza paghe)"
              onClick={() => setExcluded([...selIds], [...bkTasks, ...bkAnnual], true)} data-testid="bulk-exclude">🚫 Non previsti</button>}
            {isAdmin && <button className="btn sm" disabled={selIds.size === 0 || (bkTasks.size === 0 && bkAnnual.size === 0)}
              title="Riattiva gli adempimenti spuntati per i clienti selezionati"
              onClick={() => setExcluded([...selIds], [...bkTasks, ...bkAnnual], false)} data-testid="bulk-include">♻ Riattiva</button>}
          </div>
          </details>
          <div className="contab-table-wrap">
          <table className="table" style={{ minWidth: 1150 }} data-testid="contab-grid">
            <thead>
              <tr>
                <th style={{ width: 28 }}><input type="checkbox" checked={tabClients.length > 0 && selIds.size === tabClients.length}
                  onChange={(e) => setSelIds(e.target.checked ? new Set(tabClients.map((c) => c.id)) : new Set())} data-testid="grid-select-all" /></th>
                <th style={{ minWidth: 180 }}>Cliente</th>
                {MONTHS.map((m) => <th key={m} style={{ textAlign: "center" }}>{m}</th>)}
                {annualTasks.map((t) => <th key={t.key} style={{ textAlign: "center" }} title={t.label}>{t.label.split(" ")[1] || t.label} <span className="muted">({t.when})</span></th>)}
                <th style={{ textAlign: "center" }} title="Avanzamento su quanto dovuto fino al mese corrente">Stato</th>
              </tr>
            </thead>
            <tbody>
              {tabClients.map((c) => {
                const cm = cMonthly(c);
                const ds = dueStats(c, year, cm, cAnnual(c));
                return (
                  <tr key={c.id} data-testid={`contab-row-${c.id}`}>
                    <td><input type="checkbox" checked={selIds.has(c.id)} onChange={(e) => setSelIds((s) => { const n = new Set(s); e.target.checked ? n.add(c.id) : n.delete(c.id); return n; })} data-testid={`row-select-${c.id}`} /></td>
                    <td>
                      <b>{c.name}</b>
                      {isAdmin ? (
                        <select value={c.regime} onChange={(e) => setRegimeBulk([c.id], e.target.value || null)} style={{ display: "block", fontSize: 11, marginTop: 3, padding: "1px 4px" }} data-testid={`row-regime-${c.id}`}>
                          {REGIME_ORDER.map((r) => <option key={r} value={r}>{tasks[r].label}</option>)}
                          <option value="solo_consulenza">Solo consulenza</option>
                          <option value="">— rimuovi regime</option>
                        </select>
                      ) : (
                        <div className="muted" style={{ fontSize: 11 }}>{tasks[c.regime]?.label}</div>
                      )}
                      {(c.excluded || []).length > 0 && (
                        <div className="muted" style={{ fontSize: 10 }} data-testid={`excluded-note-${c.id}`}>
                          🚫 senza: {(c.excluded || []).map((k) => (tasks[c.regime]?.monthly || []).concat(tasks[c.regime]?.annual || []).find((t) => t.key === k)?.label || k).join(", ")}
                        </div>
                      )}
                    </td>
                    {MONTHS.map((_, i) => {
                      const period = `m${i + 1}`;
                      const done = doneInPeriod(c, period, cm);
                      return (
                        <td key={period} style={{ textAlign: "center", padding: 4 }}>
                          <button onClick={() => setCell({ client: c, period, label: `${MONTHS[i]} ${year}` })}
                            className="contab-cell-btn"
                            style={{ ...cellColor(done, cm.length), cursor: "pointer" }}
                            data-testid={`cell-${c.id}-${period}`}>
                            {done === cm.length ? "✓" : `${done}/${cm.length}`}
                          </button>
                        </td>
                      );
                    })}
                    {annualTasks.map((t) => {
                      const chk = c.checks[`a:${t.key}`];
                      return (
                        <td key={t.key} style={{ textAlign: "center", padding: 4 }}>
                          <button onClick={() => toggle(c, "a", t.key, !chk)}
                            className="contab-cell-btn"
                            title={chk ? `Fatto da ${chk.done_by} il ${fmtDate(chk.done_at)}` : `${t.label} — da fare`}
                            style={{ ...cellColor(chk ? 1 : 0, 1), cursor: "pointer" }}
                            data-testid={`annual-${c.id}-${t.key}`}>
                            {chk ? "✓" : "—"}
                          </button>
                        </td>
                      );
                    })}
                    <td style={{ textAlign: "center", padding: 4 }} title={ds.due > 0 ? `${ds.done}/${ds.due} adempimenti dovuti fino a oggi` : "Nessun adempimento ancora dovuto per quest'anno"} data-testid={`progress-${c.id}`}>
                      {ds.due > 0 ? <Donut ratio={ds.ratio} testid={`donut-${c.id}`} /> : <span className="muted">—</span>}
                    </td>
                  </tr>
                );
              })}
              {tabClients.length === 0 && <tr><td colSpan={16} className="muted">Nessun cliente in questa vista.{isAdmin ? ' Assegna i regimi dal tab "Da assegnare".' : ""}</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {cell && (
        <Modal title={`${cell.client.name} — ${cell.label}`} onClose={() => setCell(null)}>
          <p className="muted" style={{ marginTop: 0 }}>Regime {tasks[cell.client.regime]?.label}. Spunta gli adempimenti completati:</p>
          {(tasks[cell.client.regime]?.monthly || []).map((t) => {
            const excluded = (cell.client.excluded || []).includes(t.key);
            const chk = cell.client.checks[`${cell.period}:${t.key}`];
            return (
              <label key={t.key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 4px", borderBottom: "1px solid var(--border)", cursor: "pointer", opacity: excluded ? 0.5 : 1 }}>
                <input type="checkbox" checked={!!chk} disabled={excluded} onChange={(e) => toggle(cell.client, cell.period, t.key, e.target.checked)} data-testid={`task-check-${t.key}`} />
                <span style={{ flex: 1, textDecoration: excluded ? "line-through" : "none" }}>{t.label}{excluded && <span className="muted" style={{ fontSize: 11 }}> · non previsto per questo cliente</span>}</span>
                {!excluded && chk && <span className="muted" style={{ fontSize: 11 }}>{chk.done_by} · {fmtDate(chk.done_at)}</span>}
                {isAdmin && (
                  <button className="btn sm" style={{ fontSize: 11, opacity: .85 }} onClick={(e) => { e.preventDefault(); setExcluded([cell.client.id], [t.key], !excluded); }} data-testid={`task-exclude-${t.key}`}>
                    {excluded ? "♻ Riattiva" : "🚫 Non previsto"}
                  </button>
                )}
              </label>
            );
          })}
          <div className="modal-actions"><button className="btn" onClick={() => setCell(null)}>Chiudi</button></div>
        </Modal>
      )}
    </div>
  );
}
